import React, { useState, useEffect } from 'react';
import stored from 'components/hoc/stored';
import { first } from '../../../utilities';
import UseProfile from './UseProfile';
import UseExisting from './UseExisting';
import UseUpload from './UseUpload';
import UseGenerated from './UseGenerated';
import FileGallery from './FileGallery';
import PropTypes from 'prop-types';
import ResumeModal from '../dashboard/Resume/ResumeModal';
import { GENERATED_RESUME } from 'consts';
function ResumeInput(props) {
  const [fileName, setFileName] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);
  const [resumeType, setResumeType] = useState('remove_resume');
  const [inputKey, setInputKey] = useState(Math.random());
  const [fileIcon, setFileIcon] = useState(null);
  const validFileTypes = ['pdf', 'doc', 'docx'];
  const { profileResume, existingResume, lastBidResume } = props;

  const resetFileInput = () => {
    setInputKey(Math.random());
  };

  const extractFileName = path => {
    return (path || '').split(/(\\|\/)/g).pop();
  };

  const extractFileType = name => {
    return (name || '').split('.').pop();
  };

  const uploadFile = e => {
    const file = first(e.target.files);
    // we reach this if you upload a file, upload a second file and hit 'cancel'
    if (!file) {
      return;
    }

    // By using setResumeType() here we ensure that the button is only
    // highlighted when there is a change in the input. This prevents
    // the button from being highlighted when it is clicked, but the
    // user cancels instead of uploading a file.
    setResumeType('use_uploaded');
    setFileName(file.name);
    setFileIcon(extractFileType(file.name));
    setFileSizeError(file.size === 0);
    setFileTypeError(
      validFileTypes.indexOf(extractFileType(file.name.toLowerCase())) === -1,
    );
  };

  const viewGeneratedResumeModal = () => {
    setFileTypeError(false);
    setFileSizeError(false);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    // If the user uploads or uses recent resume
    // and then clicks on generate resume, but closes the modal
    // we don't want to highlight the use generated button.
    if (fileName !== GENERATED_RESUME) {
      setResumeType(resumeType);
    } else {
      // The user has already selected the generate resume button
      // and has clicked it again and closed the modal. Keep the
      // button selected instead of guessing whether they want to
      // upload or use most recent resume. This allows the user
      // to preview their resume multiple times before submission.
      setResumeType('use_generated');
    }

    setOpenModal(!openModal);
  };

  // TODO: remove this once we have reactified the bids page.
  const disableSubmit = () => {
    document.querySelectorAll('[type="submit"]').forEach(btn => {
      btn.disabled = true;
    });
  };

  // TODO: remove this once we have reactified the bids page.
  const enableSubmit = () => {
    document.querySelectorAll('[type="submit"]').forEach(btn => {
      btn.disabled = false;
    });
  };

  useEffect(() => {
    const resume = existingResume || lastBidResume;
    switch (resumeType) {
      case 'use_profile':
        // This is for a resume
        // from the talent profile page
        resetFileInput();
        setFileSizeError(false);
        setFileTypeError(false);
        setFileName(props.safeDisplayName);
        setFileIcon(extractFileType(profileResume));
        break;
      case 'use_existing':
        // This covers a scenario where there is an existing resume
        // for a bid that is in the draft status. We want to show them
        // that resume instead of the talents last resume.
        resetFileInput();
        setFileSizeError(false);
        setFileTypeError(false);
        setFileName(extractFileName(resume));
        setFileIcon(extractFileType(resume));
        break;
      case 'remove_resume':
        setFileName('');
        setFileIcon('');
        resetFileInput();
        setFileTypeError(false);
        break;
      case 'use_generated':
        resetFileInput();
        setOpenModal(false);
        setFileName(GENERATED_RESUME);
        setFileIcon(extractFileType(GENERATED_RESUME));
        break;
    }
  }, [
    resumeType,
    profileResume,
    existingResume,
    lastBidResume,
    props.safeDisplayName,
  ]);

  useEffect(() => {
    fileTypeError ? disableSubmit() : enableSubmit();
  }, [fileTypeError]);

  useEffect(() => {
    fileSizeError ? disableSubmit() : enableSubmit();
  }, [fileSizeError]);

  useEffect(() => {
    // Default to use existing button if there is a resume already available.
    if (extractFileName(existingResume) || extractFileName(lastBidResume)) {
      setResumeType('use_existing');
    }
  }, [existingResume, lastBidResume]);

  return (
    <div>
      <div className="bid-detail-layout container pt-3">
        <div className="box box-lg box-shadow-sm mt-5 mb-5" id="drop-area">
          <div className="ie-alert hidden">
            <p className="alert alert-danger">
              Unable to upload this file, please ensure the file you are
              uploading is not currently open, being used by another application
              or blank.
            </p>
          </div>
          <div>
            Resume{' '}
            <small className="text-alt">
              valid file types are .pdf .doc .docx
            </small>
          </div>
          {fileTypeError ? (
            <div id="invalid-file-type-msg" className="alert alert-danger">
              {' '}
              Unsupported file format. Please choose a PDF or Microsoft Word
              (doc, docx) format.
            </div>
          ) : (
            ''
          )}
          {fileSizeError ? (
            <div className="alert alert-danger">
              Oops! It looks like the file you uploaded is empty!
            </div>
          ) : (
            ''
          )}
          <div className="input-area visible">
            {profileResume && (
              <UseProfile
                useProfile={resumeType === 'use_profile'}
                onClick={() => setResumeType('use_profile')}
                profileResume={profileResume}
                safeDisplayName={props.safeDisplayName}
              />
            )}
            {lastBidResume && (
              <UseExisting
                useExisting={resumeType === 'use_existing'}
                onClick={() => setResumeType('use_existing')}
              />
            )}
            <UseUpload
              useUpload={resumeType === 'use_uploaded'}
              inputKey={inputKey}
              uploadFile={uploadFile}
              fileName={fileName}
            />
            <UseGenerated
              useGenerated={resumeType === 'use_generated'}
              onClick={viewGeneratedResumeModal}
            />
            {openModal && (
              <ResumeModal
                handleClose={handleModalClose}
                handleUseResume={() => setResumeType('use_generated')}
                isBidSource
                redactInfo
              />
            )}
          </div>
          <input
            type="hidden"
            id="resume_action"
            name="bid[resume_action]"
            value={resumeType}
          />
          <div className="missing-label top" id="resume-message">
            Missing
          </div>
          {/use_(profile|existing|uploaded|generated)/.test(resumeType) ? (
            <FileGallery
              className="mt-4"
              fileIcon={fileIcon}
              fileName={fileName}
              onRemove={() => setResumeType('remove_resume')}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

ResumeInput.propTypes = {
  existingResume: PropTypes.string,
  lastBidResume: PropTypes.string,
  profileResume: PropTypes.string,
  safeDisplayName: PropTypes.string,
};

export default stored(ResumeInput);
